import { useAudio } from "@/contexts/audio-context";
import { cn } from "@/lib/utils";
import { GroupedSegment } from "@/utils/group-segments";

interface ChatMessageProps {
    group: GroupedSegment;
    onMessageClick?: (timestamp: number) => void;
    searchTerm?: string;
    isHighlighted?: boolean;
}

function formatText(text: string): string {
    const trimmedText = text.trim();
    const endsWithPunctuation = /[.,!?;:]$/.test(trimmedText);

    if (endsWithPunctuation) {
        return trimmedText + " ";
    }
    return trimmedText + "\n";
}

function highlightText(text: string, searchTerm?: string): React.ReactNode {
    if (!searchTerm || searchTerm.trim() === "") {
        return text;
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
            return (
                <mark
                    key={index}
                    className="rounded bg-gradient-to-r from-yellow-200 to-yellow-300 px-1 font-semibold text-gray-900 shadow-sm dark:from-yellow-700 dark:to-yellow-600 dark:text-gray-100"
                >
                    {part}
                </mark>
            );
        }
        return part;
    });
}

// ponytail: 8 colors then they repeat; the name stays unique. Add a color if a jury regularly exceeds 8 voices.
const SPEAKER_BUBBLES = [
    "bg-sky-100 text-sky-950 dark:bg-sky-950/40 dark:text-sky-50",
    "bg-emerald-100 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-50",
    "bg-amber-100 text-amber-950 dark:bg-amber-950/40 dark:text-amber-50",
    "bg-violet-100 text-violet-950 dark:bg-violet-950/40 dark:text-violet-50",
    "bg-rose-100 text-rose-950 dark:bg-rose-950/40 dark:text-rose-50",
    "bg-cyan-100 text-cyan-950 dark:bg-cyan-950/40 dark:text-cyan-50",
    "bg-orange-100 text-orange-950 dark:bg-orange-950/40 dark:text-orange-50",
    "bg-lime-100 text-lime-950 dark:bg-lime-950/40 dark:text-lime-50"
];

export function ChatMessage({ group, onMessageClick, searchTerm, isHighlighted }: ChatMessageProps) {
    const { setCurrentTime } = useAudio();
    const multi = Boolean(group.speaker);
    const isAgent = !multi && group.channel === 1;
    const bubble = multi ? SPEAKER_BUBBLES[(group.speakerIndex ?? 0) % SPEAKER_BUBBLES.length] : null;

    const formattedText = group.texts.map(formatText).join("").trim();

    const messageId = `message-time-${Math.floor(group.startTime)}`;

    const handleClick = () => {
        // Mettre à jour le paramètre 't' dans l'URL avec le timestamp
        // const newSearchParams = new URLSearchParams(searchParams);
        // newSearchParams.set("t", group.startTime.toString());
        // setSearchParams(newSearchParams);

        // Mettre à jour le contexte audio
        setCurrentTime(group.startTime);

        // Appeler l'ancienne fonction de callback si elle existe
        if (onMessageClick) {
            onMessageClick(group.startTime);
        }
    };

    return (
        <div
            className={cn("flex w-full gap-2", !multi && isAgent ? "justify-end" : "justify-start")}
            id={messageId}
            data-start-time={group.startTime}
            data-end-time={group.endTime}
            onClick={handleClick}
            style={{ cursor: "pointer" }}
        >
            <div
                className={cn(
                    "max-w-[80%] rounded-lg px-4 py-2 transition-all duration-100",
                    bubble,
                    !multi && (isAgent ? "bg-primary text-primary-foreground" : "bg-muted"),
                    isHighlighted && multi && "scale-[1.02] ring-2 ring-primary/40",
                    isHighlighted && !multi && isAgent && "translate-x-[-2%] scale-[1.02] bg-gradient-to-r from-primary/90 to-primary shadow-lg ring-2 ring-primary/50",
                    isHighlighted &&
                        !multi &&
                        !isAgent &&
                        "translate-x-[2%] scale-[1.02] border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg dark:border-blue-600 dark:from-blue-900/20 dark:to-purple-900/20"
                )}
            >
                {group.speaker ? <p className="mb-0.5 text-xs font-medium">{group.speaker}</p> : null}
                <p className="whitespace-pre-wrap break-words text-sm">{highlightText(formattedText, searchTerm)}</p>
            </div>
        </div>
    );
}
